import clsx from 'clsx'
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import { BasicStyledComponent, DSRangeValue } from 'shared-definitions/types'
import { useWindowEventListener } from '../hooks/use-window-event-listener'
import styles from './RangeInputDiscovery.module.css'

interface RangeInputDiscoveryProps {
  constraints: DSRangeValue
  value: DSRangeValue
  onChange: (value: DSRangeValue) => void
  onDynamicChange?: (value: DSRangeValue) => void
  disableMinButton?: boolean
  disableMaxButton?: boolean
  variant?: 'default' | 'compact'
  renderMinDotLabel?: (value: DSRangeValue) => ReactNode
  renderMaxDotLabel?: (value: DSRangeValue) => ReactNode
}

interface DragInitialState {
  type: 'min' | 'max'
  x: number
}

const RangeInputDiscovery: React.VFC<RangeInputDiscoveryProps & BasicStyledComponent> = ({
  className,
  constraints,
  value,
  onChange,
  onDynamicChange,
  disableMinButton,
  disableMaxButton,
  renderMinDotLabel,
  renderMaxDotLabel,
  variant = 'default',
}) => {
  const [min, setMin] = useState(value.min)
  const [max, setMax] = useState(value.max)
  const [delta, setDelta] = useState(0)
  const [dragging, setDragging] = useState<DragInitialState | null>(null)
  const refHandleMin = useRef<HTMLButtonElement>(null)
  const refHandleMax = useRef<HTMLButtonElement>(null)
  const refTrack = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (onDynamicChange && dragging && delta) {
      if (dragging.type === 'max') {
        onDynamicChange({ min, max: max + delta })
      }
      if (dragging.type === 'min') {
        onDynamicChange({ min: min + delta, max })
      }
    }
  }, [onDynamicChange, dragging, delta])

  useEffect(() => {
    setMin(value.min)
  }, [value.min])

  useEffect(() => {
    setMax(value.max)
  }, [value.max])

  function calculateClickPercentage(event: React.MouseEvent<HTMLElement>): number {
    const canvas = event.currentTarget
    const rect = canvas.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = (clickX / canvas.clientWidth) * 100
    return percentage
  }

  useWindowEventListener(['mousedown', 'touchstart'], event => {
    if (!event.target || !(event.target instanceof Node)) {
      return
    }

    const x = event instanceof MouseEvent ? event.screenX : event.touches[0].screenX

    if (refHandleMin.current?.isSameNode(event.target)) {
      setDragging({ type: 'min', x })
    }
    if (refHandleMax.current?.isSameNode(event.target)) {
      setDragging({ type: 'max', x })
    }
  })

  useWindowEventListener(['mousemove', 'touchmove'], event => {
    if (!dragging || !refTrack.current) {
      return
    }

    const currentX = event instanceof MouseEvent ? event.screenX : event.touches[0].screenX
    const width = refTrack.current.clientWidth
    const percentDifference = (currentX - dragging.x) / width
    const pointsDifference = Math.round((constraints.max - constraints.min) * percentDifference)

    if (dragging.type === 'min') {
      const newMin = Math.max(constraints.min, Math.min(min + pointsDifference, max))
      setDelta(newMin - min)
    }
    if (dragging.type === 'max') {
      const newMax = Math.min(constraints.max, Math.max(max + pointsDifference, min))
      setDelta(newMax - max)
    }
  })

  useWindowEventListener('touchcancel', () => {
    if (!dragging) {
      return
    }

    setDelta(0)
    setDragging(null)
  })

  useWindowEventListener(['mouseup', 'touchend'], () => {
    if (!dragging) {
      return
    }

    if (dragging.type === 'min') {
      const newMin = value.min + delta
      setMin(newMin)
      onChange({ min: newMin, max: value.max })
    }
    if (dragging.type === 'max') {
      const newMax = value.max + delta
      setMax(newMax)
      onChange({ min: value.min, max: newMax })
    }

    setDelta(0)
    setDragging(null)
  })

  return (
    <div
      className={clsx(styles.container, className, {
        [styles.compact]: variant === 'compact',
        [styles.default]: variant === 'default',
      })}
    >
      {!disableMinButton && (
        <button
          ref={refHandleMin}
          type="button"
          className={styles.handle}
          aria-label="Min"
          style={
            {
              '--shift': (
                (min - constraints.min + (dragging?.type === 'min' ? delta : 0)) /
                (constraints.max - constraints.min)
              ).toFixed(4),
            } as CSSProperties
          }
        >
          {renderMinDotLabel?.({ min: min + (dragging?.type === 'min' ? delta : 0), max })}
        </button>
      )}
      {!disableMaxButton && (
        <button
          ref={refHandleMax}
          type="button"
          className={styles.handle}
          aria-label="Max"
          style={
            {
              '--shift': (
                (max - constraints.min + (dragging?.type === 'max' ? delta : 0)) /
                (constraints.max - constraints.min)
              ).toFixed(4),
            } as CSSProperties
          }
        >
          {renderMaxDotLabel?.({ min, max: max + (dragging?.type === 'max' ? delta : 0) })}
        </button>
      )}
      <button
        style={
          {
            '--min': (
              (min - constraints.min + (dragging?.type === 'min' ? delta : 0)) /
              (constraints.max - constraints.min)
            ).toFixed(4),
            '--max': (
              (max - constraints.min + (dragging?.type === 'max' ? delta : 0)) /
              (constraints.max - constraints.min)
            ).toFixed(4),
          } as CSSProperties
        }
        ref={refTrack}
        type="button"
        className={styles.track}
        aria-label="Track"
        onClick={e => {
          if (disableMaxButton === disableMinButton) {
            // we have both buttons enabled, so we ignore the click (it will be handled by the drag)
            // or we have both buttons disabled, so we don't need to do anything
            return
          }

          const clickPercentage = calculateClickPercentage(e)
          if (disableMaxButton) {
            // we have only min button enabled, so we need to calculate min value
            const newValue = Math.round(
              (clickPercentage / 100) * (constraints.max - constraints.min) + constraints.min
            )
            onChange({ min: newValue, max: value.max })
          }
          if (disableMinButton) {
            // we have only max button enabled, so we need to calculate max value
            const newValue = Math.round(
              (clickPercentage / 100) * (constraints.max - constraints.min) + constraints.min
            )
            onChange({ min: value.min, max: newValue })
          }
        }}
      />
    </div>
  )
}

export default RangeInputDiscovery
